import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  fetchAndPersistIncomingDisplayName,
  getDisplayIdentity,
} from "../utils/displayIdentity";
import { getIncomingDisplayName, isDelegatedSession } from "../utils/common";

/**
 * Resolves avatar / greeting identity for the current session.
 * When incoming !== outgoing, uses the logged-in user's name (not the rep being viewed).
 * Exposes `isLoading` while rep_info or incoming display name is still resolving.
 */
export function useDisplayIdentity() {
  const selectedName = useSelector((state) => state.repEmails.selectedName);
  const repInfoLoading = useSelector((state) => state.repEmails.isLoading);

  const delegated = isDelegatedSession();
  const needsIncomingResolve =
    delegated && !getIncomingDisplayName();

  const [incomingResolving, setIncomingResolving] = useState(needsIncomingResolve);

  useEffect(() => {
    if (!needsIncomingResolve) {
      setIncomingResolving(false);
      return;
    }

    let cancelled = false;
    setIncomingResolving(true);
    fetchAndPersistIncomingDisplayName()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setIncomingResolving(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [needsIncomingResolve]);

  const identity = useMemo(() => {
    const repInfoPending =
      !delegated && repInfoLoading && !String(selectedName || "").trim();

    return getDisplayIdentity(selectedName, {
      repInfoPending,
      incomingPending: needsIncomingResolve && incomingResolving,
    });
  }, [
    delegated,
    selectedName,
    repInfoLoading,
    needsIncomingResolve,
    incomingResolving,
  ]);

  return identity;
}
